import type { RoundingMode } from '@risksmart-app/domain/src/types/risk-score-breakdown';
import { useCallback, useEffect, useState } from 'react';

export type RecalculationIndication =
  | 'none'
  | 'outstanding'
  | 'in_progress'
  | 'failed';

interface Options {
  /** Rounding mode persisted on `aggregation_org`; `undefined` until loaded. */
  storedMode: RoundingMode | undefined;
  /** A recalculation started from this session has not yet resolved. */
  inProgress: boolean;
}

interface Result {
  indication: RecalculationIndication;
  /** The mode the current or most recent run this session started with. */
  runMode: RoundingMode | undefined;
  /**
   * A run is underway and a different mode has been saved since it started.
   * The run keeps the mode it started with; the saved one applies from the
   * next run (RSP-7300).
   */
  savedModeAppliesNextRun: boolean;
  /** Record that a run started. Pass the stored mode at that moment. */
  markStarted: (modeStartedWith: RoundingMode | undefined) => void;
  /**
   * Record that a recalculation finished. Pass the mode it ran WITH — the
   * stored mode captured when it started — not the stored mode at completion:
   * a mode saved mid-run applies only from the next run (RSP-7300).
   */
  markRecalculated: (modeRecalculatedWith: RoundingMode | undefined) => void;
  /**
   * Record that a run ended without completing (RSP-7301). Pass the mode it
   * started with, so the indication can say which mode the rows it did write
   * were scored with. Cleared by the next completed run.
   */
  markFailed: (modeRunWith: RoundingMode | undefined) => void;
}

/**
 * Nothing persists when risk scores were last recalculated, or with which mode
 * (see the M6 report). Until it does, the "mode at last recalculation" is a
 * session-scoped stand-in: the first stored mode this session observed, moved
 * forward each time a run completes here. A reload therefore forgets an
 * outstanding recalculation — the always-visible inertness copy in the panel
 * carries that truth; this indication is additive. The failed state is
 * session-scoped for the same reason.
 */
export const useRecalculationIndication = ({
  storedMode,
  inProgress,
}: Options): Result => {
  const [modeAtLastRecalculation, setModeAtLastRecalculation] =
    useState(storedMode);
  const [runMode, setRunMode] = useState<RoundingMode | undefined>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (modeAtLastRecalculation === undefined && storedMode !== undefined) {
      setModeAtLastRecalculation(storedMode);
    }
  }, [modeAtLastRecalculation, storedMode]);

  const markStarted = useCallback(
    (modeStartedWith: RoundingMode | undefined) => {
      setRunMode(modeStartedWith);
      setFailed(false);
    },
    []
  );

  const markRecalculated = useCallback(
    (modeRecalculatedWith: RoundingMode | undefined) => {
      setFailed(false);
      if (modeRecalculatedWith !== undefined) {
        setModeAtLastRecalculation(modeRecalculatedWith);
      }
    },
    []
  );

  const markFailed = useCallback((modeRunWith: RoundingMode | undefined) => {
    if (modeRunWith !== undefined) {
      setRunMode(modeRunWith);
    }
    setFailed(true);
  }, []);

  const outstanding =
    storedMode !== undefined &&
    modeAtLastRecalculation !== undefined &&
    storedMode !== modeAtLastRecalculation;

  const savedModeAppliesNextRun =
    inProgress &&
    runMode !== undefined &&
    storedMode !== undefined &&
    storedMode !== runMode;

  const indication: RecalculationIndication = inProgress
    ? 'in_progress'
    : failed
      ? 'failed'
      : outstanding
        ? 'outstanding'
        : 'none';

  return {
    indication,
    runMode,
    savedModeAppliesNextRun,
    markStarted,
    markRecalculated,
    markFailed,
  };
};
